import React, { useState, useEffect, useCallback, useRef } from "react";
import { addSyllabus, deleteSyllabus } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import "../superadmin/AdminPanel.css";

function AdministrativeSyllabusAdminPanel({ darkMode }) {
  const { threeTable, fetchThreeTable, fetchThreeTableSection, threeTableHasMore } = useGlobalData();
  const [jobs, setJobs] = useState([]);
  const [formData, setFormData] = useState({ jobSlug: "" });
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const listRef = useRef(null);
  const PAGE_LIMIT = 29;
  const role = localStorage.getItem("role");

  useEffect(() => {
    if (!threeTable?.jobs?.length) {
      fetchThreeTable();
    }
  }, [threeTable?.jobs?.length, fetchThreeTable]);

  useEffect(() => {
    setJobs(threeTable.jobs || []);
  }, [threeTable.jobs]);

  // Initial load (global state)
  useEffect(() => {
    let ignore = false;
    async function fetchInitial() {
      setIsLoading(true);
      try {
        await fetchThreeTableSection("syllabuses", false, { limit: PAGE_LIMIT, offset: 0 });
      } finally {
        if (!ignore) setIsLoading(false);
      }
    }
    fetchInitial();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Infinite scroll fetch
  const fetchMoreSyllabuses = useCallback(async () => {
    setIsFetchingMore(true);
    try {
      const offset = Array.isArray(threeTable.syllabuses) ? threeTable.syllabuses.length : 0;
      await fetchThreeTableSection("syllabuses", false, { limit: PAGE_LIMIT, offset });
    } finally {
      setIsFetchingMore(false);
    }
  }, [fetchThreeTableSection, threeTable.syllabuses]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!threeTableHasMore.syllabuses || isFetchingMore) return;
    const el = listRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 200) {
      fetchMoreSyllabuses();
    }
  }, [threeTableHasMore.syllabuses, isFetchingMore, fetchMoreSyllabuses]);

  // Attach scroll event
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await addSyllabus(formData);
    alert("Syllabus added!");
    setFormData({ jobSlug: "" });
    // Refresh global state (replace with first page)
    await fetchThreeTableSection("syllabuses", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this syllabus?")) {
      await deleteSyllabus(id);
      alert("Syllabus deleted!");
      // Refresh global state (replace with first page)
      await fetchThreeTableSection("syllabuses", true, { limit: PAGE_LIMIT, offset: 0 });
    }
  };

  const getJobTitle = (jobSlug) => jobs.find((job) => job.slug === jobSlug)?.title || jobSlug;
  const filteredSyllabuses = Array.isArray(threeTable.syllabuses)
    ? threeTable.syllabuses.filter((syllabus) => !formData.jobSlug || syllabus.jobSlug === formData.jobSlug)
    : [];

  if (role !== "administrative") {
    return <div className="admin-panel-error">❌ Access denied! Only Administrative users can access this panel.</div>;
  }

  return (
    <div className="resource-admin-panel resource-admin-panel--emerald">
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">Administrative Syllabus Admin Panel</h2>
          <p className="resource-admin-panel__subtext">Syllabus records ke liye same polished workflow diya gaya hai, jisme save, review, live preview aur delete actions ek clean layout me milte hain.</p>
        </div>
        <div className="resource-admin-panel__pill">Syllabus</div>
      </div>

      <form className="resource-admin-panel__form" onSubmit={handleSubmit}>
        <div className="resource-admin-panel__field">
          <label>Job:</label>
          <select
            value={formData.jobSlug}
            onChange={(e) => setFormData({ ...formData, jobSlug: e.target.value })}
            required
          >
            <option value="">Select Job</option>
            {jobs.map((job) => (
              <option key={job.slug} value={job.slug}>
                {job.title}
              </option>
            ))}
          </select>
        </div>
        <button type="submit">Add Syllabus</button>
      </form>

      <div className="resource-admin-panel__list" ref={listRef} style={{ maxHeight: "800px", overflowY: "auto" }}>
        <h3>Syllabus Entries</h3>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <ul>
            {filteredSyllabuses.map((syllabus) => {
              const isPending = syllabus.status === "pending";
              const statusColor = isPending ? "#f7b500" : "#2ecc40";
              return (
                <li key={syllabus._id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span>{getJobTitle(syllabus.jobSlug)}</span>
                  <span style={{
                    background: statusColor,
                    color: "#fff",
                    borderRadius: 6,
                    padding: "2px 10px",
                    fontWeight: 600,
                    fontSize: 13
                  }}>
                    {syllabus.status === "pending" ? "Pending" : "Approved"}
                  </span>
                  {isPending && (
                    <button onClick={() => handleDelete(syllabus._id)} style={{ marginLeft: 8 }}>Delete</button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {isFetchingMore && <div style={{ textAlign: "center", margin: 10 }}>Loading more...</div>}
        {!threeTableHasMore.syllabuses && !isLoading && filteredSyllabuses.length > 0 && <div style={{ textAlign: "center", margin: 10, color: "#888" }}>No more syllabuses.</div>}
      </div>
    </div>
  );
}

export default AdministrativeSyllabusAdminPanel;
